// ============================================================
// hlFootprintHistory.js
// OpenMarket → Hyperliquid historical footprint.
//
// Fetches VOLUME_PROFILE_AGG through the droplet's /om/ proxy (key attached
// server-side) and turns each per-minute [price, buy, sell, …] profile into
// the shapes the chart already understands:
//
//   profilesToPseudoTrades()  → raw trades {time, side, amount, price,
//                               tradeMcap} — feed rawTradesRef + rebuild,
//                               exactly like the Solana backfill. Bounded by
//                               the 3h futures retention → recent window only.
//
//   profilesToCellCandles()   → candles carrying _liveLevels footprint cells
//                               keyed by a native bucket, for the full 7-day
//                               depth WITHOUT going through the trade buffer
//                               (needs the renderer to paint cells on these).
//
// Pure + framework-free so it unit-tests offline and imports anywhere.
// The caller passes proxyBase (= HTTP_PROXY_BASE) so no URL is hardcoded.
// ============================================================
import { stepIdxOf, forEachStorageLevel, rekeyStep } from './candleHelpers'

const OM_EXCHANGE = 'HYPERLIQUID_FUTURES'
const OM_CATEGORY = 'PERPETUAL'
const OM_POINT_CAP = 20000            // hard per-request cap on profile points
const INTERVAL_SEC = {
  MINUTE: 60, FIVE_MINUTES: 300, FIFTEEN_MINUTES: 900, THIRTY_MINUTES: 1800,
  HOUR: 3600, FOUR_HOURS: 14400, DAY: 86400,
}

// ── Fetch a window of volume profiles, chunked under the point cap ──────
// coin: base symbol as OpenMarket knows it (e.g. 'SOL', 'BTC' — the HL
// rawSymbol, which for HL perps is the bare coin). Returns
// [{ ts, profile:[p,b,s,...] }, …] sorted ascending by ts. Never throws —
// resolves to whatever it got, logging chunk failures.
export async function fetchHlProfiles({
  coin, proxyBase, interval = 'MINUTE', hours = 24,
  nowMs = Date.now(), fetchImpl = fetch, log = () => {},
}) {
  const sec = INTERVAL_SEC[interval]
  if (!sec) throw new Error(`hlFootprint: bad interval ${interval}`)
  const endSec = Math.floor(nowMs / 1000 / sec) * sec
  const startSec = endSec - Math.round(hours * 3600)

  // Periods per call: keep points (≈ levels×3) under the cap. We don't know
  // density a-priori, so start optimistic and halve on a cap error. ~7 levels/
  // min observed for SOL → ~21 pts/period → ~950 periods fit; cap the chunk at
  // 1000 periods regardless so a dense symbol can't overshoot in one shot.
  let chunkPeriods = Math.min(1000, Math.max(1, Math.floor(OM_POINT_CAP / 21)))
  const out = []
  let cursor = startSec
  while (cursor < endSec) {
    const span = Math.min(chunkPeriods * sec, endSec - cursor)
    const qs = new URLSearchParams({
      type: 'VOLUME_PROFILE_AGG', exchange: OM_EXCHANGE, category: OM_CATEGORY,
      rawSymbol: coin, interval, from: String(cursor), period: String(span),
    })
    const url = `${proxyBase}/om/points?${qs}`
    try {
      const res = await fetchImpl(url)
      if (!res.ok) {
        // 429/5xx: the proxy already serves stale-on-error; a hard fail here
        // just means no data for this chunk. Halve and retry ONLY on the
        // point-cap error — and NEVER on 429: its body says "too many
        // requests", which the old /cap|too many/ regex matched, so a dead
        // or throttled key sent the chunker into a halving spiral (~950 →
        // 4 periods = ~125 failing requests, each behind the proxy's 13s
        // rate gate — the minutes-long stall that blocked history commits).
        const body = await res.text().catch(() => '')
        if (res.status !== 429 && /20000|point/i.test(body) && chunkPeriods > 4) {
          chunkPeriods = Math.floor(chunkPeriods / 2)
          log(`chunk too big, halving to ${chunkPeriods}`)
          continue
        }
        log(`chunk ${cursor} → ${res.status}; skipping`)
        cursor += span
        continue
      }
      const j = await res.json()
      const pts = j?.series?.[0]?.points || []
      for (const p of pts) {
        const P = p.Point || p.point || p
        const ts = P?.timestamp?.s ?? P?.timestamp
        const prof = P?.profile
        if (Number.isFinite(ts) && Array.isArray(prof) && prof.length >= 3) out.push({ ts, profile: prof })
      }
      cursor += span
    } catch (e) {
      log(`chunk ${cursor} error: ${e.message}`)
      cursor += span
    }
  }
  out.sort((a, b) => a.ts - b.ts)
  return out
}

// ── Own-capture futures profiles (stage 2 of the own-data plan) ──────────
// Single-shot fetch from OUR server's /fut/profile (swaps.db aggregation,
// 3-day depth, all venues merged). Emits the same {ts, profile} rows as the
// OM fetcher above, so profilesToCellCandles and the kline map-merge are
// untouched. Deliberately ONE request, ZERO retries, hard timeout: this
// sits inside the pair/TF commit's Promise.all, and the OM postmortem rule
// is that a history fetch there must have a bounded worst case — fail fast
// to klines, never stall the chart. timeoutMs defaults to the commit-safe
// 8s; the BACKGROUND deep-history fetch (which never blocks a commit)
// passes a longer one because a cold 72h aggregation on the droplet can
// take longer than 8s — that abort was silently downgrading every TF
// switch to plain klines.
export async function fetchFutProfiles({
  coin, proxyBase, intervalSec = 60, hours = 24, timeoutMs = 8000,
  // Window END offset in hours back from now. 0 = the newest window
  // (unchanged default). The deep-history slabber walks this backward
  // (0, 12, 24, …) so a 41h refill is several small aggregations the
  // server can answer inside one timeout, instead of one giant one that
  // silently dies and leaves klines standing.
  hoursOffset = 0,
  nowMs = Date.now(), fetchImpl = fetch, log = () => {},
}) {
  const endSec = Math.floor((nowMs / 1000 - hoursOffset * 3600) / intervalSec) * intervalSec
  const fromSec = endSec - Math.round(hours * 3600)
  const qs = new URLSearchParams({
    coin, interval: String(intervalSec), from: String(fromSec), period: String(endSec - fromSec),
  })
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null
  try {
    const res = await fetchImpl(`${proxyBase}/fut/profile?${qs}`, ctrl ? { signal: ctrl.signal } : undefined)
    if (!res.ok) { log(`fut profile → ${res.status}`); return [] }
    const j = await res.json()
    const pts = j?.series?.[0]?.points || []
    const out = []
    for (const p of pts) {
      const P = p.Point || p.point || p
      const ts = P?.timestamp?.s ?? P?.timestamp
      const prof = P?.profile
      if (Number.isFinite(ts) && Array.isArray(prof) && prof.length >= 3) out.push({ ts, profile: prof })
    }
    out.sort((a, b) => a.ts - b.ts)
    // ── The SEAM cutoff ──
    // Exact epoch-ms instant the server's data ends. The history/live split
    // used to be inferred at CANDLE granularity, which cannot be exact: the
    // server quantized its window DOWN to a slot boundary, so the in-progress
    // slot went unserved, and the client subscribed somewhere inside it —
    // leaving [boundary, subscribe) owned by nobody. It healed on the next
    // slot roll, which is why it read as an intermittent flicker in the
    // newest candle rather than a reproducible bug.
    //
    // The server now serves that partial slot (the "tip") and states where
    // it stopped. The split becomes a POINT on the timeline, not a guess:
    //   history := ts <  cutoff
    //   live    := ts >= cutoff
    // Union is everything, intersection is empty. Nothing to infer, so
    // nothing to get wrong — and it doubles as the delta-fetch watermark.
    //
    // Attached to the array rather than changing the return type, so every
    // existing call site keeps treating this as [{ts, profile}] unchanged.
    // Only meaningful for the NEWEST window: a slabbed deep fetch with
    // hoursOffset > 0 ends in the past and has no bearing on the live seam.
    out.cutoffMs = (hoursOffset === 0 && Number.isFinite(j?.cutoff)) ? j.cutoff : null
    // The price step the server ACTUALLY aggregated at (its pinned per-coin
    // grain). Cells must be keyed at this, not at the client's live grain:
    // levels sit on this lattice, so stamping a finer bucket leaves every
    // in-between fine row empty — cells every Nth row once the display goes
    // finer than this step. Keyed at the true bucket, buildDisplay coarsens
    // or spreads them like any other candle.
    out.bucket = Number(j?.bucket) > 0 ? Number(j.bucket) : 0
    log(`fut profile: ${out.length} periods${out.cutoffMs ? ` cutoff=${new Date(out.cutoffMs).toISOString().slice(11, 19)}` : ''}`)
    return out
  } catch (e) {
    log(`fut profile error: ${e.message}`)
    return []
  } finally {
    if (timer) clearTimeout(timer)
  }
}

// ── Own-capture SOLANA profiles (/sol/profile) ───────────────────────────
// The futures recipe applied to Solana: the server aggregates swaps.db into
// per-slot [price, buy, sell, …] profiles (price = native-bucket CENTRE, so
// stepIdxOf lands every level on the exact step the live path would) plus
// a per-slot OHLC, instead of streaming 6h of raw trades over the socket.
//   fromSec/toSec  window, both multiples of intervalSec (server re-floors)
//   bucket         the client's nativeBucket (0 = let the server pick by the
//                  same pickStorageBucket rule). The server echoes what it
//                  used, and cells are keyed at THAT (out.bucket).
//   cutMs          where this client's RAW coverage starts. The server
//                  serves [toSec, cut) of the still-open slot as the "tip"
//                  and returns cutoff = the exact instant its data ends —
//                  the same seam contract as /fut/profile.
// Never throws. The returned array is [{ts, profile}] with metadata riding
// on it (same trick as fetchFutProfiles' cutoffMs): status (HTTP code, -1 on
// network error/timeout — 404 means "server predates the route"), bucket,
// ref, cutoffMs, ohlcByTs (Map ts→{open,high,low,close}), tips (same shape
// as the array, the open slot only) and tipOhlc.
export async function fetchSolProfiles({
  mint, proxyBase, intervalSec = 60, fromSec, toSec, bucket = 0, cutMs = 0,
  timeoutMs = 8000, fetchImpl = fetch, log = () => {},
}) {
  const out = []
  out.status = 0; out.bucket = 0; out.ref = 0; out.cutoffMs = null; out.fastFrom = 0
  out.ohlcByTs = new Map(); out.tips = []; out.tipOhlc = new Map()
  if (!mint || !(intervalSec > 0) || !(toSec > fromSec)) return out
  const qs = new URLSearchParams({
    mint, interval: String(intervalSec), from: String(fromSec), period: String(toSec - fromSec),
  })
  if (bucket > 0) qs.set('bucket', String(bucket))
  if (cutMs > 0) qs.set('cut', String(Math.round(cutMs)))
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null
  const parse = (pts, rows, ohlcMap) => {
    for (const p of pts || []) {
      const ts = p?.timestamp?.s ?? p?.timestamp
      const prof = p?.profile
      if (!Number.isFinite(ts) || !Array.isArray(prof) || prof.length < 3) continue
      rows.push({ ts, profile: prof })
      const o = p.ohlc
      if (Array.isArray(o) && o.length === 4 && o.every(Number.isFinite)) {
        ohlcMap.set(ts, { open: o[0], high: o[1], low: o[2], close: o[3] })
      }
    }
    rows.sort((a, b) => a.ts - b.ts)
  }
  try {
    const res = await fetchImpl(`${proxyBase}/sol/profile?${qs}`, ctrl ? { signal: ctrl.signal } : undefined)
    out.status = res.status
    if (!res.ok) { log(`sol profile → ${res.status}`); return out }
    const j = await res.json()
    parse(j?.series?.[0]?.points, out, out.ohlcByTs)
    parse(j?.tip, out.tips, out.tipOhlc)
    out.bucket = Number(j?.bucket) || 0
    out.ref = Number(j?.ref) || 0
    out.cutoffMs = Number.isFinite(j?.cutoff) ? j.cutoff : null
    // solprof-20261017: ms from which the server serves bars (0 = no fast path).
    out.fastFrom = Number(j?.fastFrom) > 0 ? Number(j.fastFrom) : 0
    log(`sol profile: ${out.length}+${out.tips.length} slots bucket=${out.bucket}`)
    return out
  } catch (e) {
    out.status = -1
    log(`sol profile error: ${e.message}`)
    return out
  } finally {
    if (timer) clearTimeout(timer)
  }
}

// ── Profiles → pseudo-trades (Strategy A: reuse the trade→footprint path) ──
// Two trades per level (buy leg + sell leg), stamped inside the candle bucket.
// The footprint builder folds these into cells exactly like real swaps.
export function profilesToPseudoTrades(profiles) {
  const trades = []
  for (const per of profiles) {
    const t0 = per.ts * 1000
    let off = 0
    const prof = per.profile
    for (let i = 0; i + 2 < prof.length; i += 3) {
      const price = prof[i], buy = prof[i + 1], sell = prof[i + 2]
      if (!(price > 0)) continue
      if (buy > 0)  trades.push({ time: t0 + off++, side: 'buy',  amount: buy,  price, tradeMcap: price })
      if (sell > 0) trades.push({ time: t0 + off++, side: 'sell', amount: sell, price, tradeMcap: price })
    }
  }
  return trades
}

// ── Profiles → cell-bearing candles (Strategy B: full-depth history) ────
// Builds one PACKED candle per profile period: _native Float64Array of
// [stepIdx, buy, sell, ...] sorted by step, where stepIdx = stepIdxOf(price,
// nativeBucket) — the exact contract candleHelpers' buildDisplay/forEachLevel
// read. Marked isHistorical (the live-rebuild machinery must never touch
// them) AND _omFootprint (buildDisplay's exception + the renderer's cell
// path). ohlcByTs (optional, keyed by period SECONDS) supplies true
// open/close — pass the HL kline map; without it open/close fall back to the
// volume-weighted mean and high/low to the profile extremes.
export function profilesToCellCandles(profiles, { nativeBucket, ohlcByTs = null } = {}) {
  if (!(nativeBucket > 0)) return []
  const candles = []
  for (const per of profiles) {
    const prof = per.profile
    // Aggregate by stepIdx (several raw prices can share a step).
    const acc = new Map()   // step -> [buy, sell]
    let hi = -Infinity, lo = Infinity, vwapNum = 0, vol = 0, delta = 0
    for (let i = 0; i + 2 < prof.length; i += 3) {
      const price = prof[i], buy = prof[i + 1] || 0, sell = prof[i + 2] || 0
      if (!(price > 0) || (buy <= 0 && sell <= 0)) continue
      const step = stepIdxOf(price, nativeBucket)
      let cell = acc.get(step)
      if (!cell) { cell = [0, 0]; acc.set(step, cell) }
      cell[0] += buy; cell[1] += sell
      if (price > hi) hi = price
      if (price < lo) lo = price
      const lvol = buy + sell
      vwapNum += price * lvol; vol += lvol; delta += buy - sell
    }
    if (!acc.size || !(hi > -Infinity)) continue
    // Pack: sorted steps → Float64Array, matching packCandle's output shape.
    const steps = Array.from(acc.keys()).sort((a, b) => a - b)
    const native = new Float64Array(steps.length * 3)
    let oi = 0
    for (const s of steps) {
      const cell = acc.get(s)
      native[oi++] = s; native[oi++] = cell[0]; native[oi++] = cell[1]
    }
    const mean = vol > 0 ? vwapNum / vol : (hi + lo) / 2
    const ohlc = ohlcByTs ? ohlcByTs.get(per.ts) : null
    candles.push({
      startTime: per.ts * 1000,
      isHistorical: true,           // never rebuilt from the live trade buffer
      _omFootprint: true,           // buildDisplay exception + renderer cell path
      _liveLevels: null,
      _native: native,
      _nativeBucket: nativeBucket,
      _nativeTotalVol: vol,
      _nativeDelta: delta,
      _display: null,
      _displayBucket: null,
      _dataVersion: 1,
      _displayVersion: -1,
      open_mcap:  ohlc && ohlc.open  != null ? ohlc.open  : mean,
      // Wick = kline range EXTENDED to cover the profile extremes — live
      // parity, measured at 4.36% of overnight volume before the fix. The
      // profile merges HL + Bitunix + Coinbase (deliberate: CEX orderflow),
      // and spot venues print persistently BELOW the HL perp when funding
      // holds the perp at a premium. LIVE candles stretch their wick over
      // every merged print (addTickToLive), so live cells always sit inside
      // the wick; a kline-only history wick left that spot band poking out
      // underneath — cells floating below candles. max/min is idempotent,
      // so re-deriving OHLC from an already-extended candle changes nothing.
      high_mcap:  ohlc && ohlc.high  != null ? Math.max(ohlc.high, hi) : hi,
      low_mcap:   ohlc && ohlc.low   != null ? Math.min(ohlc.low, lo)  : lo,
      close_mcap: ohlc && ohlc.close != null ? ohlc.close : mean,
      volume_tokens: vol,
    })
  }
  return candles
}

// ── TF refold: whole slots → one coarser slot ──
// `group` = the cell candles of ONE new-TF slot, any order. Exact when the
// old TF divides the new one: a 15m slot IS its three 5m slots summed —
// levels by integer step (re-keyed by centre onto the coarsest constituent
// bucket if they differ), open = first sub-slot's open, close = last's,
// high/low = extremes. Reads STORAGE levels, never _display. The caller
// decides completeness (which slots may fold); this only sums. Marked
// _folded so the server's own cell for the slot replaces it on arrival.
export function foldCellCandles(group, startTime) {
  if (!group || !group.length) return null
  const g = group.slice().sort((a, b) => a.startTime - b.startTime)
  let gb = 0
  for (const s of g) if (s._nativeBucket > gb) gb = s._nativeBucket
  if (!(gb > 0)) return null
  const acc = new Map()
  let hi = -Infinity, lo = Infinity, vol = 0, delta = 0
  for (const s of g) {
    if (s.high_mcap > hi) hi = s.high_mcap
    if (s.low_mcap < lo) lo = s.low_mcap
    forEachStorageLevel(s, (step, buy, sell, b) => {
      const k = rekeyStep(step, b, gb)
      let cell = acc.get(k)
      if (!cell) { cell = [0, 0]; acc.set(k, cell) }
      cell[0] += buy; cell[1] += sell
      vol += buy + sell; delta += buy - sell
    })
  }
  if (!acc.size) return null
  const steps = Array.from(acc.keys()).sort((a, b) => a - b)
  const native = new Float64Array(steps.length * 3)
  let oi = 0
  for (const k of steps) {
    const cell = acc.get(k)
    native[oi++] = k; native[oi++] = cell[0]; native[oi++] = cell[1]
  }
  const first = g[0], last = g[g.length - 1]
  return {
    startTime,
    isHistorical: true,
    _omFootprint: true,
    _folded: true,
    _liveLevels: null,
    _native: native,
    _nativeBucket: gb,
    _nativeTotalVol: vol,
    _nativeDelta: delta,
    _display: null,
    _displayBucket: null,
    _dataVersion: 1,
    _displayVersion: -1,
    open_mcap: first.open_mcap,
    high_mcap: Number.isFinite(hi) ? hi : null,
    low_mcap: Number.isFinite(lo) ? lo : null,
    close_mcap: last.close_mcap,
    volume_tokens: vol,
  }
}

// ── The seam SEED: fold a tip snapshot into a live candle ──
// Companion to `cutoffMs` above. The tip is the server's aggregation of
// [slotBoundary, cutoff) — the sub-slot span the client was not yet listening
// for. It cannot enter the historical store, because the store's composer
// only admits candles whose slot has CLOSED (`startTime < currentCandleStart`)
// and the tip's slot is by definition still open. So it goes where it actually
// belongs: inside the live candle, underneath the trades the client did see.
//
// ADDITIVE, and deliberately so — the multi-tip case (a profile period finer
// than the chart TF, e.g. 15m profiles on a 30m chart) is two tips summing
// into one slot, which is exact. That makes the function itself NOT idempotent
// by design, and pushes the idempotence guarantee onto the caller: seed only
// candles built fresh in this pass, never one already on the chart. That is
// rebuildLiveFromTrades' natural shape (it mints every slot with newLiveCandle
// before folding), and seam.test.mjs pins it — ten rebuilds, one volume.
//
// Refuses on a bucket mismatch rather than converting. Both sides key levels
// by stepIdx, so a tip built on a different bucket has indices that denote
// DIFFERENT PRICES; folding it would silently deposit real volume onto wrong
// rows — a chart that lies rather than one that's missing data. The server's
// pin_bucket is what normally makes them agree; this is the assertion that it
// did. Returns true when the tip was folded, false when it was refused.
export function seedLiveFromTip(candle, tip) {
  if (!candle || !tip) return false
  const bucket = tip._nativeBucket
  if (!(bucket > 0) || bucket !== candle._nativeBucket) return false
  const a = tip._native
  if (!a || a.length < 3) return false
  if (!candle._liveLevels) candle._liveLevels = new Map()
  let folded = 0
  for (let i = 0; i + 2 < a.length; i += 3) {
    const step = a[i], buy = a[i + 1] || 0, sell = a[i + 2] || 0
    if (buy <= 0 && sell <= 0) continue
    let lvl = candle._liveLevels.get(step)
    if (!lvl) { lvl = { buy: 0, sell: 0 }; candle._liveLevels.set(step, lvl) }
    lvl.buy += buy
    lvl.sell += sell
    folded++
  }
  if (!folded) return false
  // OHLC envelope. Open comes from the tip unconditionally when the candle has
  // none yet: the tip's span ENDS where the live trades begin, so it is older
  // than every trade that will be folded after it. High/low widen, never
  // narrow. Close is left alone — the newest trade owns it, and on a tip-only
  // candle (no post-cutoff trades yet) the seed below supplies it.
  if (candle.open_mcap == null && tip.open_mcap != null) candle.open_mcap = tip.open_mcap
  if (candle.close_mcap == null && tip.close_mcap != null) candle.close_mcap = tip.close_mcap
  if (tip.high_mcap != null) {
    candle.high_mcap = candle.high_mcap == null ? tip.high_mcap : Math.max(candle.high_mcap, tip.high_mcap)
  }
  if (tip.low_mcap != null) {
    candle.low_mcap = candle.low_mcap == null ? tip.low_mcap : Math.min(candle.low_mcap, tip.low_mcap)
  }
  candle._dataVersion = (candle._dataVersion | 0) + 1
  return true
}

// Parse the OM OHLCV (TRADE_SIDE_AGNOSTIC_AGG) points into a ts→OHLC map, so
// cell-candles can carry real open/close instead of the vwap approximation.
export function parseOhlcMap(seriesJson) {
  const map = new Map()
  const pts = seriesJson?.series?.[0]?.points || []
  for (const p of pts) {
    const P = p.Point || p.point || p
    const ts = P?.timestamp?.s ?? P?.timestamp
    if (!Number.isFinite(ts)) continue
    map.set(ts, {
      open: P.open, high: P.high, low: P.low, close: P.close, volume: P.volume,
    })
  }
  return map
}

export const _internals = { OM_EXCHANGE, OM_CATEGORY, INTERVAL_SEC, OM_POINT_CAP }